import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import RequireStore from "@/components/RequireStore";
import { toast } from "sonner";
import { formatCurrency, formatDate, MONTH_NAMES } from "@/lib/format";
import MonthSelector from "@/components/MonthSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileDown, Eye, Trash2, Save, Plus } from "lucide-react";

const now = new Date();

export default function Registro() {
  const { user } = useAuth();
  const { storeId } = useStore();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async (y, m) => {
    const { data } = await api.get("/daily-records", { params: { store_id: storeId, year: y, month: m } });
    setData(data);
  }, [storeId]);

  useEffect(() => { load(year, month); }, [year, month, load]);

  const openDetail = async (date) => {
    const { data } = await api.get(`/daily-records/${date}`, { params: { store_id: storeId } });
    setDetail(data);
    setEdit({
      incasso_contanti: data.incasso_contanti, ricariche: data.ricariche,
      bar: data.bar, shop: data.shop, note: data.note,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/daily-records/${detail.date}`, {
        incasso_contanti: parseFloat(edit.incasso_contanti) || 0,
        ricariche: parseFloat(edit.ricariche) || 0,
        bar: parseFloat(edit.bar) || 0,
        shop: parseFloat(edit.shop) || 0,
        note: edit.note,
      }, { params: { store_id: storeId } });
      toast.success("Giornata aggiornata");
      setDetail(null);
      load(year, month);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeDay = async () => {
    if (!window.confirm("Eliminare definitivamente questa giornata?")) return;
    try {
      await api.delete(`/daily-records/${detail.date}`, { params: { store_id: storeId } });
      toast.success("Giornata eliminata");
      setDetail(null);
      load(year, month);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const header = ["Data", "Incasso Contanti", "Versamenti", "Ricariche", "Bar", "Shop", "Riconciliazioni", "Da Versare", "Note"];
    const rows = data.days.map((d) => [
      formatDate(d.date), d.incasso_contanti, d.versato, d.ricariche, d.bar, d.shop, d.riconciliazioni, d.da_versare,
      (d.note || "").replace(/[\n;]/g, " "),
    ]);
    const csv = [header, ...rows].map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `registro_${MONTH_NAMES[month]}_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.totals;

  return (
    <RequireStore>
    <div className="space-y-5" data-testid="registro-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Registro Giornaliero</h1>
          <p className="text-sm text-muted-foreground">Ogni riga è una giornata. Clicca per il dettaglio.</p>
        </div>
        <div className="flex items-center gap-2">
          <MonthSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
          <Button variant="secondary" className="gap-2" onClick={exportCsv} data-testid="registro-export-csv-btn">
            <FileDown className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm">
        Saldo iniziale mese: <b className="tabular-nums">{formatCurrency(data?.saldo_iniziale || 0)}</b>
        <span className="mx-2 text-muted-foreground">→</span>
        Saldo finale: <b className="tabular-nums text-primary">{formatCurrency(data?.saldo_finale || 0)}</b>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm" data-testid="registro-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3">Data</th>
              <th className="px-3 py-3 text-right">Incasso</th>
              <th className="px-3 py-3 text-right">Versato</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Ricariche</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Bar</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Shop</th>
              <th className="hidden px-3 py-3 text-right md:table-cell">Riconc.</th>
              <th className="px-3 py-3 text-right">Da versare</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data?.days?.length ? data.days.map((d) => (
              <tr key={d.date} className="cursor-pointer border-b border-border/50 hover:bg-secondary/40" onClick={() => openDetail(d.date)} data-testid={`registro-row-${d.date}`}>
                <td className="whitespace-nowrap px-3 py-3 font-medium">{formatDate(d.date)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(d.incasso_contanti)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-[#3B9BFF]">{formatCurrency(d.versato)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(d.ricariche)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(d.bar)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(d.shop)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums md:table-cell">{formatCurrency(d.riconciliazioni)}</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums text-primary">{formatCurrency(d.da_versare)}</td>
                <td className="px-3 py-3 text-right"><Eye className="ml-auto h-4 w-4 text-muted-foreground" /></td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">Nessuna giornata registrata per questo mese.</td></tr>
            )}
          </tbody>
          {t && data.days.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-primary/30 bg-secondary/60 font-bold" data-testid="registro-totals-row">
                <td className="px-3 py-3">TOTALI</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(t.incasso)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(t.versato)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(t.ricariche)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(t.bar)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(t.shop)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums md:table-cell">{formatCurrency(t.riconciliazioni)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-primary">{formatCurrency(data.saldo_finale)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Detail / edit dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="registro-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Giornata {formatDate(detail.date)}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Incasso contanti" val={edit.incasso_contanti} on={(v) => setEdit({ ...edit, incasso_contanti: v })} readonly={user?.role !== "admin"} testId="edit-incasso" />
                <EditField label="Ricariche" val={edit.ricariche} on={(v) => setEdit({ ...edit, ricariche: v })} readonly={user?.role !== "admin"} testId="edit-ricariche" />
                <EditField label="Bar" val={edit.bar} on={(v) => setEdit({ ...edit, bar: v })} readonly={user?.role !== "admin"} testId="edit-bar" />
                <EditField label="Shop" val={edit.shop} on={(v) => setEdit({ ...edit, shop: v })} readonly={user?.role !== "admin"} testId="edit-shop" />
              </div>

              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-semibold uppercase tracking-wide text-muted-foreground">Versamenti</span>
                  <span className="tabular-nums text-[#3B9BFF]">{formatCurrency(detail.versato)}</span>
                </div>
                {detail.deposits.length ? detail.deposits.map((d) => (
                  <div key={d.id} className="flex items-center justify-between py-1 text-sm">
                    <span className="tabular-nums">{formatCurrency(d.amount)} {d.riferimento && <span className="text-muted-foreground">· {d.riferimento}</span>}</span>
                    <span className="text-xs text-muted-foreground">{d.created_by}</span>
                  </div>
                )) : <span className="text-xs text-muted-foreground">Nessun versamento</span>}
              </div>

              {detail.reconciliations.length > 0 && (
                <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
                  <span className="mb-2 block font-semibold uppercase tracking-wide text-muted-foreground">Riconciliazioni</span>
                  {detail.reconciliations.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-1">
                      <span className={`tabular-nums ${r.amount < 0 ? "text-destructive" : "text-emerald-400"}`}>{formatCurrency(r.amount)}</span>
                      <span className="text-xs text-muted-foreground">{r.motivazione}</span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
                <Textarea value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} disabled={user?.role !== "admin"} data-testid="edit-note" />
              </div>

              <div className="rounded-lg bg-primary/10 px-3 py-3 text-center">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Da versare a fine giornata</span>
                <div className="font-display text-2xl font-black text-primary">{formatCurrency(detail.da_versare)}</div>
              </div>

              {user?.role === "admin" && (
                <DialogFooter className="gap-2 sm:justify-between">
                  <Button variant="ghost" className="gap-2 text-destructive" onClick={removeDay} data-testid="detail-delete-btn">
                    <Trash2 className="h-4 w-4" /> Elimina
                  </Button>
                  <Button className="gap-2 font-bold" onClick={saveEdit} data-testid="detail-save-btn">
                    <Save className="h-4 w-4" /> Salva modifiche
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </RequireStore>
  );
}

function EditField({ label, val, on, readonly, testId }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input type="number" step="0.01" value={val} onChange={(e) => on(e.target.value)} disabled={readonly} className="h-11 tabular-nums" data-testid={testId} />
    </div>
  );
}
